import React, { useEffect } from "react";
import type {
	DiscoverResult,
	ErrorResponse,
	IPaymentIntent,
	ISdkManagedPaymentIntent,
	Reader,
	Terminal,
} from "@stripe/terminal-js";
import { loadStripeTerminal } from "@stripe/terminal-js";
import { useMutation } from "@tanstack/react-query";

import { Button } from "@workspace/ui/components/button";

import { readerApi } from "../api";
import { TerminalSimulator } from "../terminal-management/simulator";
import type { TerminalReader } from "../types";
import { terminalDiscoveryConfig } from "./terminal-config";
import {
	describeConnectFailure,
	describeTerminalFailure,
	READER_CONNECT_TIMEOUT_MS,
	type TerminalFailure,
} from "./terminal-failure";

interface TerminalPaymentProps {
	amount: number;
	currency: string;
	orderId: string;
	onSuccess: (paymentIntentId: string) => void;
	onError: (error: Error) => void;
	tenant: string;
	clientSecret: string | undefined | null;

	terminals: TerminalReader[];

	/**
	 * HA-767 — true from the moment the reader starts processing the card until that finishes.
	 * Past that point the SDK cannot cancel, so the owner must refuse a Cancel rather than pretend.
	 */
	onProcessingChange?: (processing: boolean) => void;

	/**
	 * HA-767 — hands the owner a cancel for the reader, or null once this is gone. See
	 * ReaderCancelResult for what it resolves with.
	 */
	onCancelReady?: (cancel: (() => Promise<ReaderCancelResult>) | null) => void;

	/**
	 * HA-858 (S2) — the server's call: the reader shows no tip screen for this payment (no tips at the venue, a service
	 * charge on the order, a Pay Tab payment, or a tip already entered on the till). Not given: no tip screen.
	 */
	skipTipping?: boolean;
}

/**
 * HA-767 — "none": no card collection was running, so there was nothing on the reader to cancel.
 * "cancelled": a collection was running and the reader confirmed the cancel. "failed": a collection
 * was running (or processing had begun) and the reader did not cancel — it may still charge.
 */
export type ReaderCancelResult = "none" | "cancelled" | "failed";

export function TerminalPayment({
	clientSecret,
	onSuccess,
	onError,
	tenant,
	onProcessingChange,
	onCancelReady,
	skipTipping,
}: TerminalPaymentProps) {
	const terminal = React.useRef<Terminal | undefined>(undefined);
	const reader = React.useRef<Reader | undefined>(undefined);
	const [state, setState] = React.useState("idle");

	// HA-779 — why the last collect failed. Set with state "failed", which is where Retry is.
	const [failure, setFailure] = React.useState<TerminalFailure | null>(null);

	// HA-780 — why the reader did not connect (state "connect-failed"), the reader being connected so
	// Retry can connect it again, and which attempt is current so a late answer from a superseded or
	// timed-out one is ignored.
	const [connectFailure, setConnectFailure] =
		React.useState<TerminalFailure | null>(null);
	const chosenReader = React.useRef<Reader | undefined>(undefined);
	const connectAttempt = React.useRef(0);

	// HA-776 — the HA-772 simulated flag also decides who starts the collection: a real reader
	// collects as soon as it connects, a simulated one waits for the dev/e2e "Complete Order" tap.
	const { simulated } = terminalDiscoveryConfig();

	// HA-767 — where the payment is on the reader. Only "collecting" can be cancelled: once
	// processPayment has begun, the SDK has no cancel, and pretending otherwise is how a card got
	// charged after the till said it was cancelled.
	const phase = React.useRef<"idle" | "collecting" | "processing">("idle");

	const cancelCollect =
		React.useCallback(async (): Promise<ReaderCancelResult> => {
			if (phase.current === "processing") return "failed";
			if (phase.current !== "collecting" || !terminal.current) return "none";

			const result = await terminal.current.cancelCollectPaymentMethod();

			if ((result as Partial<ErrorResponse>).error) return "failed";

			phase.current = "idle";
			return "cancelled";
		}, []);

	React.useEffect(() => {
		const attempts = connectAttempt;
		return () => {
			// Leaving the screen abandons a pending connect: its answer is ignored.
			attempts.current++;
		};
	}, []);

	React.useEffect(() => {
		onCancelReady?.(cancelCollect);

		return () => {
			onCancelReady?.(null);

			// Leaving the payment screen mid-collection must not leave the reader waiting for a
			// tap that would still charge the card.
			if (phase.current === "collecting") {
				void terminal.current?.cancelCollectPaymentMethod();
			}
		};
	}, [cancelCollect, onCancelReady]);

	const [readers, setReaders] = React.useState<Reader[]>([]);

	const fetchConnectionToken = useMutation({
		...readerApi.getConnectionTokenMutation(tenant),
	});

	const discoverReaders = async () => {
		try {
			if (!terminal.current) return;

			setState("discovery");
			// HA-772 — from runtime config: real readers unless the environment asks for simulated.
			const result = await terminal.current.discoverReaders(
				terminalDiscoveryConfig(),
			);

			if ((result as Partial<ErrorResponse>).error) {
				onError(new Error((result as ErrorResponse).error.message));
			} else if ((result as DiscoverResult).discoveredReaders.length > 0) {
				const dResult = result as DiscoverResult;
				setReaders(dResult.discoveredReaders);
				setState("readers-loaded");
			}
		} catch (error) {
			setState("error");
			onError(error as Error);
		}
	};

	useEffect(() => {
		const init = async () => {
			setState("init");
			try {
				const Terminal = await loadStripeTerminal();
				terminal.current = Terminal!.create({
					onFetchConnectionToken: async () =>
						(await fetchConnectionToken.mutateAsync()).secret,
					onUnexpectedReaderDisconnect: () => alert("Reader disconnected"),
				});
				discoverReaders();
			} catch (error) {
				setState("error");
				onError(error as Error);
			}
		};
		init();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	if (!clientSecret) {
		return <div>No payment intent available.</div>;
	}

	const processExistingIntent = async () => {
		if (!terminal.current || !reader.current) return;

		// HA-779 — a failed collect (a reader timeout, a decline, a cancel on the reader) stays on this
		// screen as an error with a Retry. It is never "completed": only onSuccess says the card was
		// taken, and the owner turns that into a payment only once the server has confirmed it (HA-767).
		const fail = (
			stage: "collect" | "process",
			error: ErrorResponse["error"],
		) => {
			setFailure(describeTerminalFailure(stage, error));
			setState("failed");
		};

		setFailure(null);
		setState("reader-selected");

		try {
			phase.current = "collecting";

			const collectedPaymentMethod =
				await terminal.current.collectPaymentMethod(clientSecret, {
					config_override: { skip_tipping: skipTipping ?? true },
				});

			// Cancelled by the till (cancelCollect) while it waited: collectPaymentMethod resolves with
			// an error that is not a failure, the operator asked for it. Read through a function, since
			// cancelCollect changes it during the await above.
			const currentPhase = () => phase.current;
			if (currentPhase() !== "collecting") return;

			if ((collectedPaymentMethod as Partial<ErrorResponse>).error) {
				fail("collect", (collectedPaymentMethod as ErrorResponse).error);
				return;
			}

			const cResult = collectedPaymentMethod as {
				paymentIntent: ISdkManagedPaymentIntent;
			};

			phase.current = "processing";
			onProcessingChange?.(true);

			const processResult = await terminal.current.processPayment(
				cResult.paymentIntent,
			);

			if ((processResult as Partial<ErrorResponse>).error) {
				fail("process", (processResult as ErrorResponse).error);
				return;
			}

			const paymentIntentId = processResult as {
				paymentIntent: IPaymentIntent;
			};
			onSuccess(paymentIntentId.paymentIntent.id);
		} catch (err: unknown) {
			fail("process", {
				message: err instanceof Error ? err.message : undefined,
			} as ErrorResponse["error"]);
		} finally {
			// Processing is over either way, so Cancel (HA-767) is allowed again.
			if (phase.current === "processing") onProcessingChange?.(false);
			phase.current = "idle";
		}
	};

	async function selectReader(r: Reader) {
		chosenReader.current = r;
		const attempt = ++connectAttempt.current;
		const superseded = () => connectAttempt.current !== attempt;

		// HA-780 — a connect that fails, throws or never answers is an error with a Retry and a way back
		// to the readers; Cancel stays the till footer's (HA-767). It used to hand the error to onError
		// (the till's dead-end Failed screen), never catch a rejection, and wait for ever.
		const failConnect = (error: { code?: string; message?: string }) => {
			if (superseded()) return;
			setConnectFailure(describeConnectFailure(error));
			setState("connect-failed");
		};

		setConnectFailure(null);
		setState("reader-connecting");

		let timer: ReturnType<typeof setTimeout> | undefined;
		try {
			const connectResult = await Promise.race([
				terminal.current!.connectReader(r),
				new Promise<never>((_, reject) => {
					timer = setTimeout(
						() => reject(new Error("Connecting to the reader timed out")),
						READER_CONNECT_TIMEOUT_MS,
					);
				}),
			]);

			if (superseded()) return;

			if ((connectResult as Partial<ErrorResponse>).error) {
				failConnect((connectResult as ErrorResponse).error);
				return;
			}

			reader.current = (connectResult as { reader: Reader }).reader;
			setState("reader-selected");

			// HA-776 — a real reader has no till button to press: the card goes on the reader, and
			// Cancel (HA-767) is the way out.
			if (!simulated) void processExistingIntent();
		} catch (error) {
			failConnect({
				message: error instanceof Error ? error.message : undefined,
			});
		} finally {
			clearTimeout(timer);
		}
	}

	function chooseAnotherReader() {
		// Whatever the abandoned connect does later is ignored.
		connectAttempt.current++;
		setConnectFailure(null);
		setState("readers-loaded");
	}

	if (state === "idle") {
		return null;
	}

	if (state === "readers-loaded") {
		return (
			<div className="space-y-4 p-4">
				<h3 className="mb-2 scroll-m-20 text-2xl font-semibold tracking-tight">
					Card Payment
				</h3>
				<div className="space-y-4 rounded-xl border p-4 shadow-md">
					<h4 className="mb-2 scroll-m-20 text-xl font-semibold tracking-tight">
						Select reader
					</h4>
					<div>
						{readers.map((r) => (
							<Button
								key={r.id}
								size="lg"
								onClick={() => selectReader(r)}
								data-terminal={r.id}
							>
								{r.label}
							</Button>
						))}
					</div>
				</div>
			</div>
		);
	}

	if (state === "reader-connecting") {
		return (
			<div className="flex h-full flex-col items-center justify-center">
				<h4 className="animate-pulse scroll-m-20 text-center text-4xl font-extrabold tracking-tight text-balance">
					Connecting...
				</h4>
			</div>
		);
	}

	if (state === "reader-selected") {
		if (!simulated) {
			return (
				<div className="flex h-full flex-col items-center justify-center">
					<h4 className="animate-pulse scroll-m-20 text-center text-4xl font-extrabold tracking-tight text-balance">
						Present card on the reader
					</h4>
				</div>
			);
		}

		return (
			<div className="flex h-full flex-col items-center justify-center">
				<h4 className="animate-pulse scroll-m-20 text-center text-4xl font-extrabold tracking-tight text-balance">
					Please consult the reader to complete the payment.
				</h4>

				{/* HA-776 — the dev/e2e shortcut: simulated readers only. */}
				<Button className="mt-4" onClick={processExistingIntent}>
					Complete Order
				</Button>
			</div>
		);
	}

	if (state === "error") {
		return <div>Error!</div>;
	}

	if (state === "connect-failed" && connectFailure) {
		return (
			<div className="flex h-full flex-col items-center justify-center gap-4 p-4">
				<div
					role="alert"
					className="w-full rounded-xl border border-destructive p-4 text-center text-destructive lg:w-1/2"
				>
					<h4 className="scroll-m-20 text-2xl font-semibold tracking-tight">
						{connectFailure.title}
					</h4>
					<p className="mt-2 text-sm">{connectFailure.message}</p>
				</div>
				<div className="flex gap-2">
					<Button
						size="lg"
						onClick={() =>
							chosenReader.current && void selectReader(chosenReader.current)
						}
					>
						Retry
					</Button>
					<Button size="lg" variant="outline" onClick={chooseAnotherReader}>
						Choose another reader
					</Button>
				</div>
			</div>
		);
	}

	if (state === "failed" && failure) {
		return (
			<div className="flex h-full flex-col items-center justify-center gap-4 p-4">
				<div
					role="alert"
					className="w-full rounded-xl border border-destructive p-4 text-center text-destructive lg:w-1/2"
				>
					<h4 className="scroll-m-20 text-2xl font-semibold tracking-tight">
						{failure.title}
					</h4>
					<p className="mt-2 text-sm">{failure.message}</p>
				</div>
				<Button size="lg" onClick={() => void processExistingIntent()}>
					Retry
				</Button>
			</div>
		);
	}

	return (
		<div className="space-y-4 rounded-xl border p-4 shadow-md">
			Complete? {state}
		</div>
	);
}

export function TerminalPaymentSimulator({
	tenant,
	terminals,
}: TerminalPaymentProps) {
	return (
		<TerminalSimulator
			tenant={tenant}
			readers={terminals}
			onReaderSelect={(reader) => console.log("Selected:", reader.label)}
			onPaymentComplete={(id) => console.log("Payment:", id)}
		/>
	);
}
